if (!customElements.get('sc-review-marquee')) {
  customElements.define('sc-review-marquee', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reviewDialog = this.querySelector('.sc-reviews__dialog');
      this.keyboardInput = false;
      document.addEventListener('keydown', event => {
        if (event.key === 'Tab') this.keyboardInput = true;
      }, { capture: true, signal });
      document.addEventListener('pointerdown', event => {
        this.keyboardInput = false;
        this.lastPointer = { type: event.pointerType, x: event.clientX, y: event.clientY };
        this.rows.forEach(row => {
          row.focused = false;
          if (event.pointerType !== 'mouse') row.hovered = false;
          this.syncPause(row);
        });
        // Do not move focus into an aria-hidden repeated group.
        if (event.target.closest('[data-clone] [data-review-open]')) event.preventDefault();
      }, { capture: true, signal });
      document.addEventListener('pointermove', event => {
        this.lastPointer = { type: event.pointerType, x: event.clientX, y: event.clientY };
      }, { signal });
      this.addEventListener('click', event => {
        const row = this.rows.find(item => item.element.contains(event.target));
        if (row?.suppressClick && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
          row.suppressClick = false;
          return;
        }
        const button = event.target.closest('[data-review-open]');
        if (!button || !this.reviewDialog) return;
        const template = [...this.querySelectorAll('[data-review-template]')]
          .find(item => item.dataset.reviewTemplate === button.dataset.reviewOpen);
        if (!template) return;
        this.reviewRow = row;
        this.reviewKeyboard = event.detail === 0;
        this.reviewReturnFocus = button.closest('[data-clone]') ? button.closest('[data-row]') : button;
        if (row) { row.popup = true; this.syncPause(row); }
        this.reviewDialog.querySelector('[data-review-content]').replaceChildren(template.content.cloneNode(true));
        this.reviewDialog.showModal();
      }, { capture: true, signal });
      this.reviewDialog?.addEventListener('close', () => {
        this.closeReview();
      }, { signal });
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
      this.rows = [...this.querySelectorAll('[data-row]')].map(element => ({
        element,
        viewport: element.querySelector('.sc-reviews__viewport'),
        track: element.querySelector('.sc-reviews__track'),
        original: element.querySelector('[data-original]'),
        animation: null, distance: 0, duration: 0,
        hovered: false, focused: false, selected: false, popup: false,
        gesture: null, suppressClick: false,
      }));
      this.rows.forEach(row => {
        row.element.addEventListener('pointerenter', event => {
          row.hovered = event.pointerType === 'mouse' && this.hoverQuery.matches;
          this.syncPause(row);
        }, { signal });
        row.element.addEventListener('pointerleave', () => {
          row.hovered = false;
          this.syncPause(row);
        }, { signal });
        row.element.addEventListener('focusin', () => {
          row.focused = this.keyboardInput;
          this.syncPause(row);
        }, { signal });
        row.element.addEventListener('focusout', event => {
          row.focused = this.keyboardInput && row.element.contains(event.relatedTarget);
          this.syncPause(row);
        }, { signal });
        row.element.addEventListener('pointerdown', event => this.startDrag(row, event), { signal });
        document.addEventListener('pointermove', event => this.moveDrag(row, event), { passive: false, signal });
        document.addEventListener('pointerup', event => this.endDrag(row, event), { signal });
        document.addEventListener('pointercancel', event => this.endDrag(row, event), { signal });
        row.element.addEventListener('lostpointercapture', event => this.endDrag(row, event), { signal });
        row.element.addEventListener('dragstart', event => {
          if (row.animation) event.preventDefault();
        }, { signal });
        row.element.addEventListener('shopify:block:select', event => {
          const card = event.target.closest('[data-review-id]');
          if (!card || !row.animation) return;
          row.selected = true;
          const offset = card.offsetLeft - row.original.offsetLeft;
          const progress = row.element.dataset.row === 'bottom'
            ? 1 - offset / row.distance : offset / row.distance;
          row.animation.currentTime = progress * row.duration;
          this.syncPause(row);
        }, { signal });
        row.element.addEventListener('shopify:block:deselect', () => {
          row.selected = false;
          this.syncPause(row);
        }, { signal });
      });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.rows.forEach(row => {
        this.resizeObserver.observe(row.viewport);
        this.resizeObserver.observe(row.original);
      });
      this.motionQuery.addEventListener('change', () => this.configure(), { signal });
      this.hoverQuery.addEventListener('change', () => {
        this.rows.forEach(row => { this.refreshHover(row); this.syncPause(row); });
      }, { signal });
      window.addEventListener('blur', () => {
        this.rows.forEach(row => { this.endDrag(row); row.hovered = false; this.syncPause(row); });
      }, { signal });
      this.addEventListener('load', () => this.schedule(), { capture: true, signal });
      document.addEventListener('visibilitychange', () => this.rows.forEach(row => this.syncPause(row)), { signal });
      document.fonts.ready.then(() => {
        if (!signal.aborted) this.schedule();
      });
      this.motionButton = this.querySelector('[data-review-motion]');
      this.manuallyPaused = false;
      this.motionButton?.addEventListener('click', () => {
        this.manuallyPaused = !this.manuallyPaused;
        this.motionButton.setAttribute('aria-pressed', String(this.manuallyPaused));
        this.motionButton.setAttribute('aria-label', this.manuallyPaused ? 'Resume review movement' : 'Pause review movement');
        this.motionButton.querySelector('path').setAttribute('d', this.manuallyPaused ? 'M4 2l10 6-10 6z' : 'M4 3h3v10H4zm5 0h3v10H9z');
        this.rows.forEach(row => this.syncPause(row));
      }, { signal });
      this.configure();
    }

    closeReview() {
      const row = this.reviewRow;
      this.reviewRow = null;
      if (row) row.popup = false;
      if (this.reviewKeyboard) {
        this.keyboardInput = true;
        this.reviewReturnFocus?.focus({ preventScroll: true });
      } else {
        // Native dialog focus restoration must not turn a touch tap into keyboard pause.
        this.keyboardInput = false;
        if (row) row.focused = false;
      }
      this.rows.forEach(item => { this.refreshHover(item); this.syncPause(item); });
      this.reviewReturnFocus = null;
    }

    refreshHover(row) {
      const pointer = this.lastPointer;
      row.hovered = Boolean(this.hoverQuery.matches && pointer?.type === 'mouse' &&
        row.element.contains(document.elementFromPoint(pointer.x, pointer.y)));
    }

    startDrag(row, event) {
      if (!row.animation || row.popup || row.gesture || event.isPrimary === false || event.button !== 0) return;
      row.suppressClick = false;
      row.focused = false;
      row.gesture = { id: event.pointerId, startX: event.clientX, startY: event.clientY,
        lastX: event.clientX, horizontal: false };
      this.syncPause(row);
    }

    moveDrag(row, event) {
      const gesture = row.gesture;
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (!gesture.horizontal) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          this.endDrag(row, event);
          return; // Leave vertical panning and pinch zoom to the browser.
        }
        gesture.horizontal = true;
        row.suppressClick = true;
        row.element.dataset.dragging = 'true';
        row.element.setPointerCapture(event.pointerId);
      }
      if (event.cancelable) event.preventDefault();
      this.shiftPosition(row, event.clientX - gesture.lastX);
      gesture.lastX = event.clientX;
    }

    shiftPosition(row, pixels) {
      if (!row.animation || !row.distance || !pixels) return;
      // Change the existing animation's phase; never replace the track transform.
      const direction = row.element.dataset.row === 'bottom' ? 1 : -1;
      const time = Number(row.animation.currentTime) + direction * pixels * row.duration / row.distance;
      row.animation.currentTime = ((time % row.duration) + row.duration) % row.duration;
    }

    endDrag(row, event) {
      // Touch starts with implicit capture on the card/button. Its bubbling loss
      // when capture moves to the row is a handoff, not the end of the swipe.
      if (event?.type === 'lostpointercapture' && event.target !== row.element) return;
      const gesture = row.gesture;
      if (!gesture || (event && gesture.id !== event.pointerId)) return;
      if (event?.type === 'pointerup' && gesture.horizontal) {
        this.shiftPosition(row, event.clientX - gesture.lastX);
      }
      row.gesture = null;
      delete row.element.dataset.dragging;
      if (row.element.hasPointerCapture(gesture.id)) row.element.releasePointerCapture(gesture.id);
      this.refreshHover(row);
      this.syncPause(row);
    }

    configure() {
      const enabled = !this.motionQuery.matches && typeof Element.prototype.animate === 'function';
      this.dataset.motion = String(enabled);
      if (this.motionButton) this.motionButton.hidden = !enabled;
      this.rows.forEach(row => {
        this.endDrag(row);
        row.animation?.cancel();
        row.animation = null;
        row.distance = 0;
        row.track.querySelectorAll('[data-clone]').forEach(clone => clone.remove());
      });
      if (enabled) this.schedule();
    }

    schedule() {
      if (this.frame || this.dataset.motion !== 'true') return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.isConnected) this.rows.forEach(row => this.measure(row));
      });
    }

    measure(row) {
      const distance = row.original.getBoundingClientRect().width;
      const viewportWidth = row.viewport.getBoundingClientRect().width;
      if (!distance || !viewportWidth) return;
      const copies = Math.ceil(viewportWidth / distance) + 1;
      let clones = [...row.track.querySelectorAll('[data-clone]')];
      while (clones.length > copies) clones.pop().remove();
      while (clones.length < copies) {
        const clone = row.original.cloneNode(true);
        clone.removeAttribute('data-original');
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('button').forEach(button => button.setAttribute('tabindex', '-1'));
        clone.removeAttribute('role');
        clone.querySelectorAll('[id],[data-shopify-editor-block],[data-review-id]').forEach(element => {
          element.removeAttribute('id');
          element.removeAttribute('data-shopify-editor-block');
          element.removeAttribute('data-review-id');
        });
        row.track.append(clone);
        clones.push(clone);
      }
      const speed = Math.max(15, Math.min(75, Number(this.dataset.speed) || 35));
      const duration = distance / speed * 1000;
      if (row.animation && row.distance === distance && row.duration === duration) return;
      const progress = row.animation && row.duration
        ? (Number(row.animation.currentTime) % row.duration) / row.duration : 0;
      row.animation?.cancel();
      const frames = [{ transform: 'translateX(0px)' }, { transform: `translateX(${-distance}px)` }];
      if (row.element.dataset.row === 'bottom') frames.reverse();
      row.animation = row.track.animate(frames, { duration, iterations: Infinity, easing: 'linear' });
      row.animation.currentTime = progress * duration;
      row.distance = distance;
      row.duration = duration;
      this.syncPause(row);
    }

    syncPause(row) {
      if (!row.animation) return;
      const paused = this.manuallyPaused || row.hovered || row.focused || row.selected || row.popup || row.gesture || document.hidden;
      if (paused && row.animation.playState !== 'paused') row.animation.pause();
      if (!paused && row.animation.playState === 'paused') row.animation.play();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.rows?.forEach(row => {
        this.endDrag(row);
        row.animation?.cancel();
        row.track.querySelectorAll('[data-clone]').forEach(clone => clone.remove());
      });
    }
  });
}

